/**
 * AnimatedBackdrop — luxury animated background for editorial "white" sections.
 * Renders 3 slow-floating blurred orbs + a very faint dot grid.
 * Non-interactive (pointer-events-none). Drop inside any relatively-positioned
 * section as: <AnimatedBackdrop /> and it fills the parent.
 *
 * Variants tune the palette so it blends with each section's background.
 */

const PALETTES = {
  // Warm cream / light — best on #FAF9F6 or white sections
  cream: {
    a: "rgba(201, 169, 97, 0.22)",   // champagne gold
    b: "rgba(232, 220, 200, 0.55)",  // warm ivory
    c: "rgba(168, 160, 150, 0.20)",  // taupe
    dot: "rgba(28, 28, 28, 0.06)",
  },
  // Sand / warmer editorial — best on #F5F3EE / #F0EFEB
  sand: {
    a: "rgba(201, 169, 97, 0.28)",
    b: "rgba(220, 200, 170, 0.50)",
    c: "rgba(140, 120, 90, 0.15)",
    dot: "rgba(28, 28, 28, 0.05)",
  },
};

export default function AnimatedBackdrop({ variant = "cream", showGrid = true }) {
  const p = PALETTES[variant] || PALETTES.cream;
  return (
    <div
      aria-hidden="true"
      data-testid={`animated-backdrop-${variant}`}
      className="absolute inset-0 pointer-events-none overflow-hidden"
    >
      {/* Dot grid */}
      {showGrid && (
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: `radial-gradient(circle, ${p.dot} 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse at center, black 0%, black 50%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 0%, black 50%, transparent 100%)",
          }}
        />
      )}

      {/* Orb A — top-left */}
      <div
        className="absolute rounded-full"
        style={{
          top: "-8%",
          left: "-6%",
          width: "40vw",
          height: "40vw",
          maxWidth: "620px",
          maxHeight: "620px",
          background: p.a,
          filter: "blur(90px)",
          animation: "tt-float-a 22s ease-in-out infinite",
        }}
      />

      {/* Orb B — bottom-right */}
      <div
        className="absolute rounded-full"
        style={{
          bottom: "-10%",
          right: "-8%",
          width: "45vw",
          height: "45vw",
          maxWidth: "700px",
          maxHeight: "700px",
          background: p.b,
          filter: "blur(110px)",
          animation: "tt-float-b 28s ease-in-out infinite",
          animationDelay: "-6s",
        }}
      />

      {/* Orb C — center-right, smaller */}
      <div
        className="absolute rounded-full"
        style={{
          top: "35%",
          right: "18%",
          width: "22vw",
          height: "22vw",
          maxWidth: "340px",
          maxHeight: "340px",
          background: p.c,
          filter: "blur(70px)",
          animation: "tt-float-c 34s ease-in-out infinite",
          animationDelay: "-12s",
        }}
      />
    </div>
  );
}
